import { makeAutoObservable, action } from "mobx";
import novaApiClient from "../api/novaApiClient";
import mentionApiClient from "../api/mentionApiClient";
import {
  ChatMessage,
  NovaState,
  FrontendCommand,
} from "../types/workflow.types";
import {
  MentionItem,
  MentionState,
  MentionSearchResult,
} from "../types/mention.types";
import { SelectedTokensStore } from "./SelectedTokensStore";
import { interceptMessageForAPI } from "../utils/MessageParser";

export class NovaStore implements NovaState {
  messages: ChatMessage[] = [];
  currentThreadId: string | null = null;
  isTyping: boolean = false;
  isGenerating: boolean = false;
  error: string | null = null;

  features = {
    chatMentionsV0: true,
  };

  // Selected tokens store for managing user-selected tokens
  selectedTokensStore = new SelectedTokensStore();

  mentionState: MentionState = {
    isOpen: false,
    query: "",
    anchorCaret: 0,
    highlightKey: null,
    isLoading: false,
    error: null,
  };

  mentionItems: MentionItem[] = [];
  topTokens: MentionItem[] = [];
  topTokensLoaded: boolean = false;
  selectedTab: "all" | "basic" | "erc20" = "all";

  basicTopTokens: MentionItem[] = [];
  erc20TopTokens: MentionItem[] = [];

  get filteredItems(): MentionItem[] {
    if (!this.features.chatMentionsV0) return [];

    if (!this.mentionState.query) {
      const topTokens = this.topTokens.slice(0, 25);
      const filteredTopTokens = this.filterByTab(topTokens);

      if (filteredTopTokens.length > 0 && !this.mentionState.highlightKey) {
        this.setHighlightForTopTokens();
      }

      return filteredTopTokens;
    }

    const query = this.mentionState.query.toLowerCase();
    const filtered = this.mentionItems.filter((item) =>
      item.textValue.toLowerCase().includes(query)
    );

    const filteredByTab = this.filterByTab(filtered);

    console.log("query:", query, "filtered:", filteredByTab.length, "items");
    return filteredByTab.slice(0, 12);
  }

  filterByTab(items: MentionItem[]): MentionItem[] {
    switch (this.selectedTab) {
      case "basic":
        return items.filter((item) => item.source === "basic");
      case "erc20":
        return items.filter((item) => item.source === "erc20");
      case "all":
      default:
        return items;
    }
  }

  constructor() {
    makeAutoObservable(this);
    this.initializeStore();
    this.preloadTopTokens();
  }

  private preloadTopTokens() {
    console.log("🚀 Preloading top tokens for Nova...");

    const startPreload = async () => {
      try {
        // Load basic tokens first
        console.log("📡 Loading basic top tokens...");
        await this.loadBasicTopTokensAsync();

        // Wait a bit between calls to avoid conflicts
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Load ERC20 tokens second
        console.log("📡 Loading ERC20 top tokens...");
        await this.loadERC20TopTokensAsync();

        this.topTokensLoaded = true;
        console.log("🎉 All top tokens preloaded successfully!");
      } catch (error) {
        console.error("❌ Error preloading top tokens:", error);
      }
    };

    // Start preloading immediately
    startPreload();
  }

  private async loadBasicTopTokensAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      mentionApiClient.getTopTokens(
        (results: MentionSearchResult) => {
          this.basicTopTokens = results.basic.slice(0, 25);
          console.log(
            "✅ Basic top tokens loaded:",
            this.basicTopTokens.length
          );
          this.updateTopTokensForCurrentTab();
          resolve();
        },
        (error: string) => {
          console.error("❌ Failed to load basic top tokens:", error);
          reject(new Error(error));
        },
        (loading: boolean) => {},
        "basic"
      );
    });
  }

  private async loadERC20TopTokensAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      mentionApiClient.getTopTokens(
        (results: MentionSearchResult) => {
          this.erc20TopTokens = results.erc20.slice(0, 25);
          console.log(
            "✅ ERC20 top tokens loaded:",
            this.erc20TopTokens.length
          );
          this.updateTopTokensForCurrentTab();
          resolve();
        },
        (error: string) => {
          console.error("❌ Failed to load ERC20 top tokens:", error);
          reject(new Error(error));
        },
        (loading: boolean) => {},
        "erc20"
      );
    });
  }

  private updateTopTokensForCurrentTab() {
    let mergedTokens: MentionItem[] = [];

    switch (this.selectedTab) {
      case "basic":
        mergedTokens = this.basicTopTokens;
        break;
      case "erc20":
        mergedTokens = this.erc20TopTokens;
        break;
      case "all":
      default:
        mergedTokens = [...this.basicTopTokens, ...this.erc20TopTokens];
        break;
    }

    this.topTokens = mergedTokens;
    console.log(
      `📋 Updated top tokens for tab "${this.selectedTab}":`,
      mergedTokens.length
    );
  }

  setTopTokens = action((tokens: MentionItem[]) => {
    this.topTokens = tokens;
    console.log(
      "Top tokens set:",
      tokens.length,
      "items:",
      tokens.map((i) => `${i.label} (${i.source})`)
    );
  });

  setSelectedTab = action((tab: "all" | "basic" | "erc20") => {
    this.selectedTab = tab;

    this.mentionState.highlightKey = null;

    this.updateTopTokensForCurrentTab();

    // Relancer la recherche si le dropdown est ouvert et qu'il y a une query
    if (this.mentionState.isOpen && this.mentionState.query.trim()) {
      console.log(
        "🔄 Relaunching search for new tab:",
        tab,
        "query:",
        this.mentionState.query
      );
      this.searchMentions(this.mentionState.query);
    }
  });

  /**
   * Initialize the store with default values
   */
  private initializeStore() {
    this.messages = [];
    this.currentThreadId = null;
    this.isTyping = false;
    this.isGenerating = false;
    this.error = null;

    // Add welcome message
    this.addMessage(
      "assistant",
      "Hello! I'm Nova, an intelligent and conversational AI assistant. I can help you understand and use the SynkOps system. How can I help you today?"
    );
  }

  /**
   * Reset the store to initial state
   */
  initializeWorkflow() {
    this.initializeStore();
  }

  /**
   * Add a message to the chat
   */
  addMessage(role: "user" | "assistant", content: string, threadId?: string) {
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      role,
      content,
      timestamp: Date.now(),
      threadId: threadId || this.currentThreadId || undefined,
    };

    this.messages.push(message);
  }

  /**
   * Set the generating state
   */
  setGenerating(generating: boolean) {
    this.isGenerating = generating;
  }

  /**
   * Set the typing state
   */
  setTyping(typing: boolean) {
    this.isTyping = typing;
  }

  /**
   * Set error message
   */
  setError(error: string | null) {
    this.error = error;
  }

  /**
   * Set current thread ID
   */
  setThreadId(threadId: string | null) {
    this.currentThreadId = threadId;
  }

  /**
   * Process user input and send to Nova API
   */
  async processUserInput(message: string) {
    console.log("🤖 Nova - Processing user input:", {
      message,
    });

    try {
      const interceptedMessage = interceptMessageForAPI(
        message,
        this.selectedTokensStore.selectedTokens
      );
      console.log("🔄 Message intercepted for API:", {
        original: message,
        intercepted: interceptedMessage,
      });

      this.addMessage("user", message);

      // Set generating state
      this.setGenerating(true);
      this.setError(null);

      const response = await novaApiClient.sendMessage(
        interceptedMessage,
        this.currentThreadId || undefined
      );

      // Update thread ID from response
      if (response.thread_id) {
        this.setThreadId(response.thread_id);
      }

      // Add assistant response to chat
      this.addMessage("assistant", response.message, response.thread_id);

      // Process frontend commands if any
      if (response.frontend_commands && response.frontend_commands.length > 0) {
        await this.processFrontendCommands(response.frontend_commands);
      }
    } catch (error) {
      console.error("❌ Nova - Error processing user input:", error);
      this.setError(
        error instanceof Error ? error.message : "An error occurred"
      );
      this.addMessage(
        "assistant",
        "Sorry, I encountered an error. Please try again."
      );
    } finally {
      this.setGenerating(false);
    }
  }

  /**
   * Process frontend commands from Nova API response
   * Ensures only one action per service per message (keeps the last occurrence)
   */
  private async processFrontendCommands(commands: FrontendCommand[]) {
    console.log("🎯 Nova - Processing frontend commands:", commands);

    // Deduplicate commands by service - keep only the last occurrence of each service
    const deduplicatedCommands = this.deduplicateCommandsByService(commands);

    console.log("🎯 Nova - Deduplicated commands:", deduplicatedCommands);

    for (const command of deduplicatedCommands) {
      try {
        await this.executeFrontendCommand(command);
      } catch (error) {
        console.error(
          `❌ Nova - Error executing command ${command.name}:`,
          error
        );
      }
    }
  }

  /**
   * Deduplicate commands by service, keeping only the last occurrence of each service
   */
  private deduplicateCommandsByService(
    commands: FrontendCommand[]
  ): FrontendCommand[] {
    const serviceMap = new Map<string, FrontendCommand>();

    // Process commands in order, keeping only the last occurrence of each service
    for (const command of commands) {
      serviceMap.set(command.service, command);
    }

    // Convert back to array
    const deduplicatedCommands = Array.from(serviceMap.values());

    console.log("🔄 Nova - Deduplication result:", {
      original: commands.length,
      deduplicated: deduplicatedCommands.length,
      services: deduplicatedCommands.map((cmd) => cmd.service),
    });

    return deduplicatedCommands;
  }

  /**
   * Execute a single frontend command
   */
  private async executeFrontendCommand(command: FrontendCommand) {
    console.log(`🎯 Nova - Executing command: ${command.name}`, command);

    switch (command.type) {
      case "open_frontend":
        await this.openFrontendApp(command);
        break;
      case "navigate":
        await this.navigateToUrl(command);
        break;
      case "execute_action":
        await this.executeAction(command);
        break;
      default:
        console.warn(`⚠️ Nova - Unknown command type: ${command.type}`);
    }
  }

  /**
   * Open a frontend app based on the command with parameter support
   */
  private async openFrontendApp(command: FrontendCommand) {
    const { service, service_frontend_url, parameters } = command;

    console.log(`🚀 Nova - Opening ${service} app`, {
      service_frontend_url,
      parameters,
    });

    // Import appStore dynamically to avoid circular dependencies
    const { default: appStore } = await import("../../../stores/AppStore");

    // Map service names to app identifiers - comprehensive mapping
    const serviceMap: Record<string, string> = {
      // SynkPass variations
      SynkPass: "synkpass",
      synkpass: "synkpass",
      "synk-pass": "synkpass",

      // SynkCrypt variations
      SynkCrypt: "synkcrypt",
      synkcrypt: "synkcrypt",
      "synk-crypt": "synkcrypt",

      // SynkSmash variations
      SynkSmash: "synksmash",
      synksmash: "synksmash",
      "synk-smash": "synksmash",

      // SynkSwap variations
      SynkSwap: "synkswap",
      synkswap: "synkswap",
      "synk-swap": "synkswap",

      // SynkStore variations
      SynkStore: "synkstore",
      synkstore: "synkstore",
      "synk-store": "synkstore",

      // SynkChainScan variations
      SynkChainScan: "synkchainscan",
      synkchainscan: "synkchainscan",
      "synk-chain-scan": "synkchainscan",

      // SynkTodo variations
      SynkTodo: "synktodo",
      synktodo: "synktodo",
      "synk-todo": "synktodo",

      // Other possible services
      SynkOS: "synkos",
      synkos: "synkos",
    };

    const appId = serviceMap[service];
    if (appId) {
      try {
        // Build URL with parameters if they exist
        const urlWithParams = this.buildUrlWithParameters(
          service,
          service_frontend_url,
          parameters
        );

        console.log(`🚀 Nova - Launching app: ${service} -> ${appId}`);
        console.log(`🌐 Nova - URL with parameters: ${urlWithParams}`);

        // Launch app with custom URL if parameters are present
        if (parameters && Object.keys(parameters).length > 0) {
          appStore.launchApp(appId, { customUrl: urlWithParams });
        } else {
          appStore.launchApp(appId);
        }

        console.log(`✅ Nova - Successfully opened ${service} (${appId})`);
      } catch (error) {
        console.error(`❌ Nova - Failed to open ${service} (${appId}):`, error);
      }
    } else {
      console.warn(`⚠️ Nova - Unknown service: ${service}`);
      console.log(`🔍 Nova - Available services:`, Object.keys(serviceMap));
    }
  }

  /**
   * Build URL with parameters for specific services
   */
  private buildUrlWithParameters(
    service: string,
    baseUrl?: string,
    parameters?: Record<string, any>
  ): string {
    if (!parameters || Object.keys(parameters).length === 0) {
      return baseUrl || this.getDefaultServiceUrl(service);
    }

    // Use base URL from command or fallback to default service URL
    const url = baseUrl || this.getDefaultServiceUrl(service);

    // Build query string from parameters
    const queryParams = new URLSearchParams();

    // Map parameters to URL-friendly format
    const mappedParams = this.mapParametersForService(service, parameters);

    Object.entries(mappedParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  /**
   * Get default service URL
   */
  private getDefaultServiceUrl(service: string): string {
    const defaultUrls: Record<string, string> = {
      SynkPass: "https://synkpass.synk.ws",
      SynkCrypt: "https://synkcrypt.synk.ws",
      SynkSmash: "https://smash.synk.ws",
      SynkSwap: "https://swap.synk.ws",
      SynkStore: "https://store.synk.ws",
      SynkChainScan: "https://chainscan.synk.ws",
      SynkTodo: "https://todo.synk.ws",
      SynkOS: "https://synk.ws",
    };

    return defaultUrls[service] || "https://synk.ws";
  }

  /**
   * Map parameters to service-specific URL format
   * Automatically includes all parameters - no manual mapping needed
   */
  private mapParametersForService(
    service: string,
    parameters: Record<string, any>
  ): Record<string, any> {
    // Automatically include all parameters - no need for manual mapping
    // This makes the system dynamic and future-proof
    const mapped: Record<string, any> = {};

    Object.entries(parameters).forEach(([key, value]) => {
      // Only include non-null, non-undefined values
      if (value !== null && value !== undefined) {
        mapped[key] = value;
      }
    });

    console.log(`🔄 Nova - Auto-mapping parameters for ${service}:`, {
      original: parameters,
      mapped: mapped,
    });

    return mapped;
  }

  /**
   * Navigate to a URL
   */
  private async navigateToUrl(command: FrontendCommand) {
    const { service_frontend_url } = command;

    if (service_frontend_url) {
      console.log(`🌐 Nova - Opening URL: ${service_frontend_url}`);
      window.open(service_frontend_url, "_blank");
    }
  }

  private async executeAction(command: FrontendCommand) {
    const { name, parameters } = command;

    console.log(`⚡ Nova - Executing action: ${name}`, parameters);

    switch (name) {
      case "create_swap":
        break;
      case "encrypt_file":
        break;
      case "share_file":
        break;
      default:
        console.log(`ℹ️ Nova - Action ${name} executed (no specific handler)`);
    }
  }

  detectMention(text: string, caretPosition: number) {
    if (!this.features.chatMentionsV0) return null;

    let lastAt = -1;
    for (let i = caretPosition - 1; i >= 0; i--) {
      if (text[i] === "@") {
        lastAt = i;
        break;
      }
      if (text[i] === " " || text[i] === "\n" || text[i] === "\t") {
        break;
      }
    }

    if (lastAt === -1) return null;

    const query = text.slice(lastAt + 1, caretPosition);

    if (query.includes(" ") || query.includes("\n") || query.includes("\t")) {
      return null;
    }

    return { anchorCaret: lastAt, query };
  }

  handleMessageChange = (value: string, caretPosition: number) => {
    if (!this.features.chatMentionsV0) return;

    const mention = this.detectMention(value, caretPosition);

    if (mention) {
      if (!this.mentionState.isOpen) {
        console.log("Dropdown open=true");
        this.openMention(mention.query, mention.anchorCaret);
      } else {
        console.log("query:", mention.query);
        this.updateMentionQuery(mention.query);
      }
    } else {
      if (this.mentionState.isOpen) {
        console.log("Dropdown open=false");
        this.closeMention();
      }
    }
  };

  openMention = action((query: string, anchorCaret: number) => {
    this.mentionState.isOpen = true;
    this.mentionState.query = query;
    this.mentionState.anchorCaret = anchorCaret;
    this.mentionState.error = null;
    this.mentionState.highlightKey = null;

    if (query.trim()) {
      this.searchMentions(query);
    }
  });

  updateMentionQuery = action((query: string) => {
    this.mentionState.query = query;
    this.mentionState.error = null;
    this.mentionState.highlightKey = null;

    if (query.trim()) {
      this.searchMentions(query);
    }
  });

  searchMentions = (query: string) => {
    const shouldSearchBasic =
      this.selectedTab === "all" || this.selectedTab === "basic";
    const shouldSearchERC20 =
      this.selectedTab === "all" || this.selectedTab === "erc20";

    if (!shouldSearchBasic && !shouldSearchERC20) {
      this.setMentionItems([]);
      return;
    }

    mentionApiClient.searchCurrencies(
      query,
      (results: MentionSearchResult) => {
        console.log("results:", {
          basic: results.basic.length,
          erc20: results.erc20.length,
          merged: results.merged.length,
        });

        let filteredResults = results.merged;
        if (this.selectedTab === "basic") {
          filteredResults = results.basic;
        } else if (this.selectedTab === "erc20") {
          filteredResults = results.erc20;
        }

        this.setMentionItems(filteredResults);
      },
      (error: string) => {
        console.error("Mention search error:", error);
        this.setMentionError(error);
      },
      (loading: boolean) => {
        this.setMentionLoading(loading);
      },
      shouldSearchBasic,
      shouldSearchERC20
    );
  };

  setMentionItems = action((items: MentionItem[]) => {
    this.mentionItems = items;

    if (items.length > 0 && !this.mentionState.highlightKey) {
      this.mentionState.highlightKey = items[0].id;
    }
  });

  setHighlightForTopTokens = action(() => {
    if (this.topTokens.length > 0 && !this.mentionState.highlightKey) {
      this.mentionState.highlightKey = this.topTokens[0].id;
    }
  });

  setMentionLoading = action((loading: boolean) => {
    this.mentionState.isLoading = loading;
  });

  setMentionError = action((error: string) => {
    this.mentionState.error = error;
  });

  retryMentionSearch = () => {
    this.searchMentions(this.mentionState.query);
  };

  closeMention = action(() => {
    if (this.mentionState.isOpen) {
      console.log("Dropdown open=false");
      this.mentionState.isOpen = false;
      this.mentionState.query = "";
      this.mentionState.anchorCaret = 0;
      this.mentionState.highlightKey = null;
      this.mentionState.isLoading = false;
      this.mentionState.error = null;

      mentionApiClient.cancelPendingRequests();
    }
  });

  resetMentionState = () => {
    this.mentionState.isOpen = false;
    this.mentionState.query = "";
    this.mentionState.anchorCaret = 0;
    this.mentionState.highlightKey = null;
  };

  selectItem = (itemId: string, currentText: string, caretPosition: number) => {
    console.log("select:", { id: itemId });

    // Search in both mentionItems and topTokens
    let item = this.mentionItems.find((i) => i.id === itemId);
    if (!item) {
      item = this.topTokens.find((i) => i.id === itemId);
    }

    if (!item) return { newText: currentText, newCaretPosition: caretPosition };

    this.selectedTokensStore.addSelectedToken(item);

    const beforeMention = currentText.slice(0, this.mentionState.anchorCaret);
    const afterMention = currentText.slice(caretPosition);
    // Use new format: @symbol/mode instead of @id[source]
    const newText =
      beforeMention + `@${item.label}/${item.source} ` + afterMention;

    this.closeMention();

    return {
      newText,
      newCaretPosition:
        beforeMention.length + `@${item.label}/${item.source} `.length,
    };
  };

  setDropdownOpen = action((isOpen: boolean) => {
    this.mentionState.isOpen = isOpen;
    if (!isOpen) {
      this.mentionState.highlightKey = null;
    }
  });

  setHighlightKey = action((key: string | null) => {
    this.mentionState.highlightKey = key;
  });

  navigateDown = action(() => {
    const currentIndex = this.filteredItems.findIndex(
      (item) => item.id === this.mentionState.highlightKey
    );
    const nextIndex =
      currentIndex < this.filteredItems.length - 1 ? currentIndex + 1 : 0;
    this.mentionState.highlightKey = this.filteredItems[nextIndex]?.id || null;
  });

  navigateUp = action(() => {
    const currentIndex = this.filteredItems.findIndex(
      (item) => item.id === this.mentionState.highlightKey
    );
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : this.filteredItems.length - 1;
    this.mentionState.highlightKey = this.filteredItems[prevIndex]?.id || null;
  });
}
